import { useUser } from 'src/Hooks/useUser'
import { useTypedSelector } from 'src/State/RootReducer'
import { hasAccessToLuoxDirectMarketing } from '../../utils'
import { useFeatureFlagsContext } from 'src/Libs/feature-flags/FeatureFlagsContext'
import useExtensionAccess from './use-extension-access'
import { OstromSoftwareSupport, TibberSoftwareSupport } from '../../config'
import { useInexogyAccess } from '../inexogy'
import { useAccessLuoxDynamicTariff } from '../luox'
import { useAi360Access } from '../ai360'
import { isFlowTheme } from 'src/Libs/utils'

interface ReturnType {
  hasLuoxDirectMarketingAccess: boolean
  hasLuoxDynamicTariffAccess: boolean
  hasOstromAccess: boolean
  hasInexogyAccess: boolean
  hasTibberAccess: boolean
  hasAi360PowerFixAccess: boolean
  hasAi360PowerShareAccess: boolean
}

/**
 * This hook checks for various extensions if they should be visible based on the user's access rights and feature flags.
 *
 * Flow only offers the dynamic tariff extensions, the remaining ones stay exclusive to e3dc.
 *
 * Note: In an upcoming ticket the visibility is likely moved to the backend.
 */
export const useExtensionVisibility = (): ReturnType => {
  const { userData } = useUser()
  const currentInstallation = useTypedSelector(state => state.storage.installation)
  const hasLuoxDirectMarketingAccess = hasAccessToLuoxDirectMarketing(currentInstallation, userData)
  const hasLuoxDynamicTariffAccess = useAccessLuoxDynamicTariff(currentInstallation, userData)
  const hasInexogyAccess = useInexogyAccess(currentInstallation, userData)
  const { hasAi360PowerFixAccess, hasAi360PowerShareAccess } = useAi360Access(currentInstallation, userData)
  const {
    featureFlags: { isOStromEnabled, isTibberEnabled },
  } = useFeatureFlagsContext()
  const { hasAccess: hasOstromAccess } = useExtensionAccess(isOStromEnabled, OstromSoftwareSupport.Year, OstromSoftwareSupport.MonthPatch)
  const { hasAccess: hasTibberAccess } = useExtensionAccess(isTibberEnabled, TibberSoftwareSupport.Year, OstromSoftwareSupport.MonthPatch)

  return {
    hasLuoxDirectMarketingAccess: !isFlowTheme && hasLuoxDirectMarketingAccess,
    hasInexogyAccess: !isFlowTheme && hasInexogyAccess,
    hasLuoxDynamicTariffAccess,
    hasOstromAccess,
    hasTibberAccess,
    hasAi360PowerFixAccess,
    hasAi360PowerShareAccess,
  }
}
